import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest } from 'rxjs';
import { AuthService } from 'src/app/pages/core/abstraction/auth.service';
import { CryptoService } from 'src/app/pages/core/abstraction/crypto.service';
import {
  IaUSTData,
  IaUSTProfit,
  ICryptoPortfolio,
  ICryptoPrice,
  ICryptoPrices,
} from 'src/app/pages/core/models/Crypto';
import { Utils } from 'src/app/pages/core/utils/Utils';
import { ConfirmSwapDialogComponent } from '../../../swap/confirm-swap-dialog/confirm-swap-dialog.component';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import {
  AlertDialogComponent,
  AlertDialogData,
} from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';

enum OPERATION_TYPE {
  BUY = 'BUY',
  SELL = 'SELL',
}
@Component({
  selector: 'app-fiat-investment-popup',
  templateUrl: './fiat-investment-popup.component.html',
  styleUrls: ['./fiat-investment-popup.component.scss'],
})
export class FiatInvestmentPopupComponent implements OnInit {
  @Input() selectedCoin: string;
  @Output() closeEvent = new EventEmitter();
  @Input() austProfit: IaUSTProfit;
  currencyTicker = 'ARS';
  stakingOperation = 0;
  srcCoin: string;
  destCoin: string;
  currentCoinPair: string;
  totalFiatBalance = 0;
  selectedSection: number;
  balances: ICryptoPortfolio[];
  austPrice: ICryptoPrice;
  prices: ICryptoPrices;
  quantity: FormControl = new FormControl('', [Validators.required]);
  austData: IaUSTData;
  //austProfit: aUSTProfit;
  chartData: { name: string; value: number }[] = [];
  dates: string[] = [];
  dailyGain: any[];
  //INVESTMENT DATA
  totalInvested = 0;
  totalAustEquivalent = 0;
  estimatedProfitsPercent = 0;
  fiatProfits = 0;
  selectedMonth = new Date().getMonth() + 1;
  oldestProfitDate = new Date();

  // CHART OPTIONS
  view: any[] = [100, 50];
  showXAxis = false;
  showYAxis = false;
  gradient = false;
  showLegend = false;
  colorScheme: Color = {
    name: 'chartScheme',
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#423A7F'],
  };

  constructor(
    public authService: AuthService,
    private cryptoService: CryptoService,
    private dialog: MatDialog,
    private eRef: ElementRef,
  ) {}

  // @HostListener('document:click', ['$event'])
  // clickout(event) {
  //   if (!this.eRef.nativeElement.contains(event.target)) {
  //     this.closeDialog();
  //   }
  // }

  ngOnInit(): void {
    this.currencyTicker = this.selectedCoin.replace('$', 'S');
    // this.totalInvested =
    //   this.currencyTicker === 'ARS' ? this.austProfit.totalArs : this.austProfit.totalUsd;
    // this.totalInvested = +Math.abs(this.totalInvested).toFixed(3);
    // if (this.totalInvested) this.selectedSection = 0;
    combineLatest([
      this.cryptoService.prices,
      this.authService.myCryptoPortfolio,
      this.authService.loggedUserObs,
    ]).subscribe((res) => {
      this.prices = res[0];
      this.balances = res[1];
      this.totalFiatBalance = res[2]?.balance.fiat[this.currencyTicker];
      this.estimatedProfitsPercent = this.austData.real * 100;

      this.dailyGain = this.austProfit.dailyGain?.filter((dailyGain) => {
        return dailyGain[this.currencyTicker.toLowerCase()] !== 0;
      });
      this.oldestProfitDate = this.dailyGain.reduce((prev, curr: any) => {
        const currDate = new Date(curr.date);
        return currDate < prev ? currDate : prev;
      }, new Date());
      this.formatChartData(this.dailyGain);
      this.setCoinInfo();
    });
  }

  closeDialog() {
    this.closeEvent.emit();
  }

  setCoinInfo() {
    this.austPrice = this.prices['AUST_' + this.currencyTicker];
    if (this.balances.length) {
      this.totalAustEquivalent =
        +this.balances.find((balance) => balance.ticker === 'AUST_' + this.currencyTicker)
          ?.stocks || 0;
      this.fiatProfits =
        this.currencyTicker === 'ARS' ? this.austProfit.gainArs : this.austProfit.gainUsd;
      this.totalInvested =
        this.currencyTicker === 'ARS' ? this.austProfit.totalArs : this.austProfit.totalUsd;

      this.totalInvested = +Math.abs(this.totalInvested).toFixed(3);
      this.fiatProfits = +Math.abs(this.fiatProfits).toFixed(3);

      if (this.selectedSection === undefined) {
        if (!this.totalInvested) {
          this.selectedSection = 1;
        } else {
          this.selectedSection = 0;
        }
      }
    }
    this.currentCoinPair = 'AUST_' + this.currencyTicker;
    if (this.stakingOperation === 0) {
      this.srcCoin = this.currencyTicker;
      this.destCoin = 'AUST';
    } else {
      this.srcCoin = 'AUST';
      this.destCoin = this.currencyTicker;
    }
  }

  chartIsEmpty() {
    return (
      !this.chartData.length ||
      (this.chartData[0]?.value === 0 &&
        this.chartData[1]?.value === 0 &&
        this.chartData[2]?.value === 0 &&
        this.chartData[3]?.value === 0)
    );
  }

  setSelectedMonth(month) {
    //No seleccionar meses futuros ni inferiores al registro mas antiguo
    if (month <= new Date().getMonth() + 1 && this.oldestProfitDate.getMonth() + 1 < month) {
      this.selectedMonth = month;
      this.formatChartData(this.dailyGain);
    }
  }

  formatChartData(chartData: { date: string; ars: number; usd: number }[]) {
    const histories: { name: string; value: number }[] = [];
    const date = new Date();
    date.setMonth(this.selectedMonth - 1);
    const month = date.getMonth() + 1;
    const today = date.getDate();
    const dates = ['1 al 7', '8 al 14', '15 al 21', '22 al 28'];
    const values = [];
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];
    const actualMonth = chartData.filter((data) => +data.date.split('-')[1] === month);
    const previousMonth = chartData.filter(
      (data) => +data.date.split('-')[1] === (month === 1 ? 12 : month - 1),
    );
    if (today >= 1 && today <= 7) {
      dates.push(dates.shift());
      for (let i = 0; 4 > i; i++) {
        if (i === 3) {
          dates[i] = `${dates[i]} ${months[date.getMonth()]}`;
        } else if (i !== 2) {
          dates[i] = `${dates[i]} ${months[date.getMonth() === 0 ? 11 : date.getMonth() - 1]}`;
        } else {
          dates[i] = `22 al ${new Date(
            date.getFullYear(),
            month === 1 ? 12 : month - 1,
            0,
          ).getDate()} ${months[date.getMonth() === 0 ? 11 : date.getMonth() - 1]}`;
        }
      }
    } else if (today >= 8 && today <= 14) {
      dates.push(dates.shift());
      dates.push(dates.shift());
      for (let i = 0; 4 > i; i++) {
        if (i > 1) {
          dates[i] = `${dates[i]} ${months[date.getMonth()]}`;
        } else if (i === 0) {
          dates[i] = `${dates[i]} ${months[date.getMonth() === 0 ? 11 : date.getMonth() - 1]}`;
        } else {
          dates[i] = `22 al ${new Date(
            date.getFullYear(),
            month === 1 ? 12 : month - 1,
            0,
          ).getDate()} ${months[date.getMonth() === 0 ? 11 : date.getMonth() - 1]}`;
        }
      }
    } else if (today >= 15 && today <= 21) {
      dates.unshift(dates.pop());
      for (let i = 0; 4 > i; i++) {
        if (i !== 0) {
          dates[i] = `${dates[i]} ${months[date.getMonth()]}`;
        } else {
          dates[i] = `22 al ${new Date(
            date.getFullYear(),
            month === 1 ? 12 : month - 1,
            0,
          ).getDate()} ${months[date.getMonth() === 0 ? 11 : date.getMonth() - 1]}`;
        }
      }
    } else {
      for (let i = 0; 4 > i; i++) {
        if (i !== 3) {
          dates[i] = `${dates[i]} ${months[date.getMonth()]}`;
        } else {
          dates[i] = `22 al ${new Date(date.getFullYear(), month, 0).getDate()} ${
            months[date.getMonth()]
          }`;
        }
      }
    }

    if (today >= 1 && today <= 7) {
      for (let i = 0; dates.length > i; i++) {
        if (i === 0) {
          let acc = 0;
          if (previousMonth.length) {
            const period = previousMonth.filter(
              (month) => +month.date.split('-')[2] >= 8 && +month.date.split('-')[2] <= 14,
            );
            if (period.length) {
              period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
            }
          }
          values[i] = acc;
        }
        if (i === 1) {
          let acc = 0;
          if (previousMonth.length) {
            const period = previousMonth.filter(
              (month) => +month.date.split('-')[2] >= 15 && +month.date.split('-')[2] <= 21,
            );
            if (period.length) {
              period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
            }
          }
          values[i] = acc;
        }
        if (i === 2) {
          let acc = 0;
          if (previousMonth.length) {
            const period = previousMonth.filter(
              (month) => +month.date.split('-')[2] >= 22 && +month.date.split('-')[2] <= 31,
            );
            if (period.length) {
              period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
            }
          }
          values[i] = acc;
        }
        if (i === 3) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 1 && +month.date.split('-')[2] <= 7,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
      }
    }
    if (today >= 8 && today <= 14) {
      for (let i = 0; dates.length > i; i++) {
        if (i === 0) {
          if (previousMonth.length) {
            const period = previousMonth.filter(
              (month) => +month.date.split('-')[2] >= 15 && +month.date.split('-')[2] <= 21,
            );
            let acc = 0;
            if (period.length) {
              period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
            }
            values[i] = acc;
          }
        }
        if (i === 1) {
          let acc = 0;
          if (previousMonth.length) {
            const period = previousMonth.filter(
              (month) => +month.date.split('-')[2] >= 22 && +month.date.split('-')[2] <= 31,
            );
            if (period.length) {
              period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
            }
          }
          values[i] = acc;
        }
        if (i === 2) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 1 && +month.date.split('-')[2] <= 7,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
        if (i === 3) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 8 && +month.date.split('-')[2] <= 14,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
      }
    }
    if (today >= 15 && today <= 21) {
      for (let i = 0; dates.length > i; i++) {
        if (i === 0) {
          let acc = 0;
          if (previousMonth.length) {
            const period = previousMonth.filter(
              (month) => +month.date.split('-')[2] >= 22 && +month.date.split('-')[2] <= 31,
            );
            if (period.length) {
              period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
            }
          }
          values[i] = acc;
        }
        if (i === 1) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 1 && +month.date.split('-')[2] <= 7,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
        if (i === 2) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 8 && +month.date.split('-')[2] <= 14,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
        if (i === 3) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 15 && +month.date.split('-')[2] <= 21,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
      }
    }
    if (today >= 22 && today <= 31) {
      for (let i = 0; dates.length > i; i++) {
        if (i === 0) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 1 && +month.date.split('-')[2] <= 7,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
        if (i === 1) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 8 && +month.date.split('-')[2] <= 14,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
        if (i === 2) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 15 && +month.date.split('-')[2] <= 21,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
        if (i === 3) {
          const period = actualMonth.filter(
            (month) => +month.date.split('-')[2] >= 22 && +month.date.split('-')[2] <= 31,
          );
          let acc = 0;
          if (period.length) {
            period.forEach((value) => (acc += value[this.currencyTicker.toLocaleLowerCase()]));
          }
          values[i] = acc;
        }
      }
    }

    this.dates = dates;
    for (let i = 0; 4 > i; i++) {
      histories[i] = { name: dates[i], value: values[i] };
    }
    this.chartData = histories;
  }

  checkIfCanSwap() {
    if (!this.authService.checkOnboardingStatus()) {
      return false;
    } else if (
      (this.stakingOperation === 0 && Number(this.quantity.value) > this.totalFiatBalance) ||
      (this.stakingOperation === 1 && Number(this.quantity.value) > this.totalInvested)
    ) {
      this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
        panelClass: 'modal-container',
        disableClose: true,
        data: {
          title: 'Fondos insuficientes',
          content: `Tus fondos no son suficientes para realizar esta operación`,
        },
      });
      return false;
    }
    return true;
  }

  swapBtnClicked() {
    if (!this.checkIfCanSwap()) return;
    const operation = this.stakingOperation === 0 ? 'BUY' : 'SELL';
    this.dialog
      .open(ConfirmSwapDialogComponent, {
        panelClass: 'modal-container',
        backdropClass: 'dialog-primary-backdrop',
        maxWidth: '90vw',
        width: '500px',
        data: {
          srcCoin: this.srcCoin,
          destCoin: this.destCoin,
          srcCoinAmount: this.getSrcCoinAmount(), // Utils.parseCurrencryToNumber(this.quantity.value),
          destCoinAmount: this.getDestCoinAmount(), //this.getTotalAmount(),
          currentCoinPair: this.currentCoinPair,
          operation,
          price: operation === OPERATION_TYPE.BUY ? this.austPrice?.buy : this.austPrice?.sell,
          custodyFee:
            operation === OPERATION_TYPE.SELL
              ? (this.austData.fee / 365) * Utils.parseCurrencryToNumber(this.quantity.value)
              : '',
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.closeDialog();
        }
      });
  }

  getSrcCoinAmount() {
    if (this.stakingOperation === 0) {
      return Math.abs(Utils.parseCurrencryToNumber(this.quantity.value));
    }
    return Math.abs(
      Utils.parseCurrencryToNumber(this.quantity.value) / Number(this.austPrice?.buy) || 0,
    );
  }

  getDestCoinAmount() {
    if (this.stakingOperation === 0) {
      return Math.abs(
        Utils.parseCurrencryToNumber(this.quantity.value) / Number(this.austPrice?.sell) || 0,
      );
    }
    return Math.abs(Utils.parseCurrencryToNumber(this.quantity.value));
  }

  setMaxAmount() {
    if (this.stakingOperation === 0) {
      this.quantity.setValue(Utils.parseNumberToCurrencyString(this.totalFiatBalance));
    } else if (this.stakingOperation === 1) {
      this.quantity.setValue(
        Utils.parseNumberToCurrencyString(
          this.currencyTicker === 'ARS' ? this.austProfit.totalArs : this.austProfit.totalUsd,
        ),
      );
    }
  }
}
